/**
 * Solicitudes list for AtencionDialog - Shows solicitudes with solucion aplicada
 */
import React, { useState } from 'react';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { FileText, CheckCircle, Plus, Loader2, MessageCircle, Phone, User } from 'lucide-react';
import VoiceInput from '../VoiceInput';

const AtencionSolicitudesList = ({ 
  solicitudes,
  loadingSolicitudes = false,
  savedSoluciones,
  onUpdateSolucion,
  onTerminarSolicitud,
  terminatingSolicitudId,
  onOpenActaDiaria,
  onOpenActaDiariaEdit,
  onPreviewImage,
  readOnly = false,
  npsHabilitado = false
}) => {
  const getEstadoBadge = (estado) => {
    if (estado === 'terminado') {
      return <Badge className="bg-blue-100 text-blue-700 text-[10px]">Terminado</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">Pendiente</Badge>;
  };

  return (
    <Card className="border-[#E8E8E7]">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <FileText size={18} className="text-[#9d8272]" />
          <span>Solicitudes ({solicitudes.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadingSolicitudes ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <Loader2 size={28} className="animate-spin mb-2 text-[#9d8272]" />
            <p className="text-sm">Cargando solicitudes...</p>
          </div>
        ) : solicitudes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hay solicitudes con evaluación &quot;procede&quot;</p>
          </div>
        ) : (
          solicitudes.map((sol) => (
            <SolicitudItem
              key={sol.id}
              sol={sol}
              savedSoluciones={savedSoluciones}
              onUpdateSolucion={onUpdateSolucion}
              onTerminarSolicitud={onTerminarSolicitud}
              terminatingSolicitudId={terminatingSolicitudId}
              onOpenActaDiaria={onOpenActaDiaria}
              onOpenActaDiariaEdit={onOpenActaDiariaEdit}
              onPreviewImage={onPreviewImage}
              getEstadoBadge={getEstadoBadge}
              readOnly={readOnly}
              npsHabilitado={npsHabilitado}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
};

// Individual solicitud item component
const SolicitudItem = ({
  sol,
  savedSoluciones,
  onUpdateSolucion,
  onTerminarSolicitud,
  terminatingSolicitudId,
  onOpenActaDiaria,
  onOpenActaDiariaEdit,
  onPreviewImage,
  getEstadoBadge,
  readOnly,
  npsHabilitado
}) => {
  const isTerminating = terminatingSolicitudId === sol.id;
  const [npsModal, setNpsModal] = useState({ open: false, destinatario: '', telefono: '' });

  const handleTerminarClick = async () => {
    const result = await onTerminarSolicitud(sol.id);
    if (result && result.nps_enviado) {
      setNpsModal({
        open: true,
        destinatario: result.nps_destinatario || '',
        telefono: result.nps_telefono || ''
      });
    }
  };
  
  // Check if solucion_aplicada is saved
  const solucionGuardada = sol.solucion_aplicada && 
    sol.solucion_aplicada.trim() !== '' &&
    savedSoluciones[sol.id] &&
    savedSoluciones[sol.id] === sol.solucion_aplicada;
  
  // Check if all actas diarias are "terminado" or "rechazado"
  const actasDiariasCompletas = !sol.actas_diarias || sol.actas_diarias.length === 0 || 
    sol.actas_diarias.every(acta => 
      acta.estado === 'terminado' || acta.acta_estado === 'rechazado_correo'
    );
  
  // Can only terminate if solucion is saved AND all actas are complete
  const canTerminar = solucionGuardada && actasDiariasCompletas;
  
  // Build tooltip message
  const getTooltipMessage = () => {
    if (!sol.solucion_aplicada || sol.solucion_aplicada.trim() === '') {
      return 'Debe ingresar la solución aplicada primero';
    }
    if (!savedSoluciones[sol.id] || savedSoluciones[sol.id] !== sol.solucion_aplicada) {
      return 'Debe guardar los cambios antes de terminar (clic en "Grabar")';
    }
    if (!actasDiariasCompletas) {
      const actasPendientes = sol.actas_diarias.filter(acta => 
        acta.estado !== 'terminado' && acta.acta_estado !== 'rechazado_correo'
      );
      return `Hay ${actasPendientes.length} acta(s) diaria(s) pendiente(s). Todas deben estar Terminadas o Rechazadas.`;
    }
    return 'Marcar esta solicitud como terminada';
  };

  return (
    <>
    <div className="border rounded-lg p-4 border-[#E8E8E7] bg-[#FAFAFA]" data-testid={`atencion-solicitud-${sol.id}`}>
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-[#9d8272]">{sol.codigo}</span>
          {getEstadoBadge(sol.estado)}
        </div>
        {!readOnly && sol.estado !== 'terminado' && (
          <TooltipProvider>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenActaDiaria(sol)}
                    className="h-7 text-xs border-[#9d8272] text-[#9d8272]"
                    data-testid={`atencion-add-acta-diaria-${sol.id}`}
                  >
                    <Plus size={12} className="mr-1" />
                    Acta Diaria
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Agregar acta diaria de trabajo</p>
                </TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    onClick={handleTerminarClick}
                    disabled={!canTerminar || isTerminating}
                    className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300"
                    data-testid={`atencion-terminar-solicitud-${sol.id}`}
                  >
                    {isTerminating ? (
                      <Loader2 size={12} className="mr-1 animate-spin" />
                    ) : (
                      <CheckCircle size={12} className="mr-1" />
                    )}
                    {isTerminating ? 'Procesando...' : (npsHabilitado && canTerminar ? 'Terminar y enviar NPS' : 'Terminar')}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{getTooltipMessage()}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        )}
      </div>

      {/* Datos de la solicitud (solo lectura) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
        <div>
          <span className="text-[#888888]">Categoría:</span>
          <p className="font-medium">{sol.solicitud?.categoria || 'N/A'}</p>
        </div>
        <div>
          <span className="text-[#888888]">Lugar:</span>
          <p className="font-medium">{sol.solicitud?.lugar || 'N/A'}</p>
        </div>
        <div>
          <span className="text-[#888888]">Item:</span>
          <p className="font-medium">{sol.solicitud?.item || 'N/A'}</p>
        </div>
        <div>
          <span className="text-[#888888]">Falla:</span>
          <p className="font-medium">{sol.solicitud?.falla || 'N/A'}</p>
        </div>
      </div>

      {/* Descripción */}
      {sol.solicitud?.descripcion && (
        <div className="mb-3">
          <span className="text-xs text-[#888888]">Descripción:</span>
          <p className="text-sm bg-white p-2 rounded border border-[#E8E8E7] whitespace-pre-wrap">
            {sol.solicitud.descripcion}
          </p>
        </div>
      )}

      {/* Fotos */}
      {sol.solicitud?.foto_url && (
        <SolicitudPhotos 
          fotoUrl={sol.solicitud.foto_url} 
          onPreview={onPreviewImage} 
        />
      )}

      {/* Solución Aplicada */}
      <div className="mb-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Solución Aplicada</Label>
          {!readOnly && sol.estado !== 'terminado' && (
            <VoiceInput
              onTranscript={(text, isFinal) => {
                if (isFinal) {
                  onUpdateSolucion(sol.id, 
                    sol.solucion_aplicada ? `${sol.solucion_aplicada} ${text}` : text
                  );
                }
              }}
            />
          )}
        </div>
        <Textarea
          value={sol.solucion_aplicada || ''}
          onChange={(e) => onUpdateSolucion(sol.id, e.target.value)}
          className={`mt-1 border-[#c5b8a2] text-sm ${readOnly || sol.estado === 'terminado' ? 'bg-gray-50' : ''}`}
          readOnly={readOnly || sol.estado === 'terminado'}
          rows={2}
          placeholder="Describa la solución aplicada..."
          data-testid={`solucion-aplicada-${sol.id}`}
        />
      </div>

      {/* Actas Diarias */}
      {sol.actas_diarias && sol.actas_diarias.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#E8E8E7]">
          <span className="text-xs font-medium text-[#888888]">Actas Diarias ({sol.actas_diarias.length}):</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {sol.actas_diarias.map((acta) => (
              <button
                key={acta.id}
                onClick={() => onOpenActaDiariaEdit(acta, sol)}
                className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs hover:bg-blue-100 transition-colors"
                data-testid={`acta-diaria-${acta.id}`}
              >
                <FileText size={12} />
                <span>Acta Diaria {acta.codigo}</span>
                {(acta.acta_estado === 'aprobado_correo' || acta.acta_estado === 'rechazado_correo') && (
                  <CheckCircle size={10} className={acta.acta_estado === 'aprobado_correo' ? 'text-green-500' : 'text-red-500'} />
                )}
                {(acta.acta_estado === 'aprobado_whatsapp' || acta.acta_estado === 'rechazado_whatsapp') && (
                  <MessageCircle size={10} className={acta.acta_estado === 'aprobado_whatsapp' ? 'text-green-600 fill-green-600' : 'text-red-500 fill-red-500'} />
                )}
                {acta.estado === 'terminado' && (
                  <span className="text-[10px] text-green-600 ml-1">✓</span>
                )}
                {acta.estado === 'rechazado' && (
                  <span className="text-[10px] text-red-600 ml-1">✗</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>

      {/* Modal confirmación NPS */}
      <Dialog open={npsModal.open} onOpenChange={(open) => !open && setNpsModal({ open: false, destinatario: '', telefono: '' })}>
        <DialogContent className="sm:max-w-md" data-testid="nps-confirmation-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle size={20} />
              NPS enviado exitosamente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 text-sm">
              <User size={16} className="text-gray-500 flex-shrink-0" />
              <span className="text-gray-600">Destinatario:</span>
              <span className="font-medium" data-testid="nps-destinatario">{npsModal.destinatario}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Phone size={16} className="text-gray-500 flex-shrink-0" />
              <span className="text-gray-600">Teléfono:</span>
              <span className="font-medium" data-testid="nps-telefono">{npsModal.telefono}</span>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setNpsModal({ open: false, destinatario: '', telefono: '' })}
              className="bg-[#9d8272] hover:bg-[#8a7060] text-white"
              data-testid="nps-modal-close-btn"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Photos component
const SolicitudPhotos = ({ fotoUrl, onPreview }) => {
  let fotos = [];
  try {
    fotos = JSON.parse(fotoUrl);
  } catch {
    fotos = [fotoUrl];
  }

  return (
    <div className="mb-3">
      <span className="text-xs text-[#888888]">Fotos:</span>
      <div className="flex gap-2 mt-1">
        {fotos.map((foto, i) => (
          <img
            key={i}
            src={foto}
            alt={`Foto ${i + 1}`}
            className="w-16 h-16 object-cover rounded border cursor-pointer hover:opacity-80"
            onClick={() => onPreview(foto)}
          />
        ))}
      </div>
    </div>
  );
};

export default AtencionSolicitudesList;
